import React, { useState, useEffect } from 'react';
import { Spinner } from 'react-bootstrap';
import { useEntidadeContext } from '../../EntidadeContext';
import { useAlertModal } from '../../AlertModalContext';
import axios from 'axios';

const ICONS = {
  image:  'M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z',
  upload: 'M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12',
  check:  'M5 13l4 4L19 7',
  trash:  'M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16',
};

const SvgIcon = ({ d, size = 14, color = 'currentColor', stroke = 2 }) => (
  <svg width={size} height={size} fill="none" stroke={color} viewBox="0 0 24 24" style={{ flexShrink: 0 }}>
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={stroke} d={d} />
  </svg>
);

const LayoutEntidadeTab = () => {
  const { currentEntidade, setCurrentEntidade } = useEntidadeContext();
  const { showAlertModal } = useAlertModal();

  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview]           = useState(null);
  const [isLoading, setIsLoading]       = useState(false);
  const [error, setError]               = useState(null);
  const [isDragging, setIsDragging]     = useState(false);

  useEffect(() => {
    if (!selectedFile) { setPreview(undefined); return; }
    const url = URL.createObjectURL(selectedFile);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [selectedFile]);

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0]);
      setError(null);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file && file.type.startsWith('image/')) {
      setSelectedFile(file);
      setError(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedFile) { setError('Selecione um arquivo de imagem para o logo.'); return; }
    if (!currentEntidade) { setError('Nenhuma entidade selecionada.'); return; }

    setIsLoading(true);
    setError(null);
    const formData = new FormData();
    formData.append('logo', selectedFile);

    try {
      const response = await axios.post(
        `/api/entidades/${currentEntidade.id}/upload-logo`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      setCurrentEntidade(response.data.entidade);
      showAlertModal('Sucesso', 'Logo atualizado com sucesso!', 'success');
      setSelectedFile(null);
    } catch (err) {
      const msg = err.response?.data?.message || 'Ocorreu um erro ao enviar o logo.';
      setError(msg);
      showAlertModal('Erro', msg, 'danger');
    } finally {
      setIsLoading(false);
    }
  };

  const currentLogoUrl = currentEntidade?.logo_url || null;
  const displaySrc     = preview || currentLogoUrl || null;

  return (
    <div style={{ fontFamily: "'Inter','Segoe UI',sans-serif" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');
        .logo-upload-input { display: none; }
        .logo-drop-zone:hover { border-color: #2563eb !important; background: #eff6ff !important; }
      `}</style>

      {/* ── HEADER ── */}
      <div style={{ background: 'linear-gradient(135deg,#1e3a5f,#2563eb)', borderRadius: '14px', padding: '20px 24px', marginBottom: '24px', display: 'flex', alignItems: 'center', gap: '14px' }}>
        <div style={{ width: '44px', height: '44px', borderRadius: '12px', backgroundColor: 'rgba(255,255,255,.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <SvgIcon d={ICONS.image} size={22} color="#fff" stroke={1.8} />
        </div>
        <div>
          <div style={{ fontSize: '16px', fontWeight: '800', color: '#fff' }}>Logo da Clínica</div>
          <div style={{ fontSize: `calc(12px * var(--font-scale))`, color: 'rgba(255,255,255,.7)' }}>Imagem usada em e-mails e documentos gerados pelo sistema</div>
        </div>
      </div>

      {/* ── INFO ── */}
      <div style={{ backgroundColor: '#eff6ff', borderRadius: '12px', border: '1px solid #bfdbfe', padding: '14px 18px', marginBottom: '24px', display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
        <svg width="18" height="18" fill="none" stroke="#2563eb" viewBox="0 0 24 24" style={{ flexShrink: 0, marginTop: '1px' }}>
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <div style={{ fontSize: `calc(12px * var(--font-scale))`, color: '#3b82f6', lineHeight: '1.6' }}>
          <strong style={{ color: '#1e40af' }}>Formatos aceitos:</strong> PNG, JPG ou GIF.
          Recomendamos uma imagem com fundo transparente (PNG) e dimensões de pelo menos{' '}
          <strong>300 × 100 px</strong> para melhor qualidade nos documentos.
        </div>
      </div>

      {/* ── FORM ── */}
      <form onSubmit={handleSubmit}>
        <div style={{ display: 'grid', gridTemplateColumns: '280px 1fr', gap: '20px', alignItems: 'start' }}>

          {/* ── PREVIEW ── */}
          <div style={{ backgroundColor: '#fff', borderRadius: '14px', border: '1px solid #e5e7eb', padding: '20px', textAlign: 'center' }}>
            <div style={{ fontSize: `calc(11px * var(--font-scale))`, fontWeight: '700', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: '14px' }}>
              {preview ? 'Pré-visualização' : 'Logo Atual'}
            </div>

            <div style={{
              width: '100%', height: '130px', borderRadius: '10px',
              backgroundColor: '#f8fafc', border: '1px solid #e5e7eb',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              overflow: 'hidden', marginBottom: '12px',
            }}>
              {displaySrc ? (
                <img src={displaySrc} alt="Logo" style={{ maxWidth: '100%', maxHeight: '120px', objectFit: 'contain' }} />
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                  <SvgIcon d={ICONS.image} size={36} color="#d1d5db" stroke={1.5} />
                  <span style={{ fontSize: `calc(11px * var(--font-scale))`, color: '#9ca3af' }}>Sem logo</span>
                </div>
              )}
            </div>

            {currentLogoUrl && !preview && (
              <span style={{ fontSize: `calc(10px * var(--font-scale))`, fontWeight: '700', padding: '3px 10px', borderRadius: '999px', backgroundColor: '#f0fdf4', color: '#16a34a', border: '1px solid #bbf7d0' }}>
                ✓ Logo configurado
              </span>
            )}

            {preview && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', alignItems: 'center' }}>
                <span style={{ fontSize: `calc(10px * var(--font-scale))`, fontWeight: '700', padding: '3px 10px', borderRadius: '999px', backgroundColor: '#eff6ff', color: '#2563eb', border: '1px solid #bfdbfe' }}>
                  Novo logo selecionado
                </span>
                <button
                  type="button"
                  onClick={() => { setSelectedFile(null); setError(null); }}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: `calc(11px * var(--font-scale))`, color: '#dc2626', background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit', padding: '2px 6px' }}
                >
                  <SvgIcon d={ICONS.trash} size={11} color="#dc2626" />
                  Remover seleção
                </button>
              </div>
            )}
          </div>

          {/* ── UPLOAD ── */}
          <div style={{ backgroundColor: '#fff', borderRadius: '14px', border: '1px solid #e5e7eb', padding: '20px' }}>
            <div style={{ fontSize: `calc(11px * var(--font-scale))`, fontWeight: '700', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: '14px' }}>
              Selecionar Novo Logo
            </div>

            {/* Drop zone */}
            <label
              htmlFor="logo-file-input"
              className="logo-drop-zone"
              onDragOver={e => { e.preventDefault(); setIsDragging(true); }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={handleDrop}
              style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                gap: '10px', padding: '36px 20px', borderRadius: '12px', cursor: 'pointer',
                border: `2px dashed ${isDragging ? '#2563eb' : '#cbd5e1'}`,
                backgroundColor: isDragging ? '#eff6ff' : '#f8fafc',
                transition: 'border-color .2s, background .2s', marginBottom: '16px',
              }}
            >
              <div style={{ width: '44px', height: '44px', borderRadius: '12px', backgroundColor: isDragging ? '#dbeafe' : '#e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'background .2s' }}>
                <SvgIcon d={ICONS.upload} size={22} color={isDragging ? '#2563eb' : '#6b7280'} stroke={1.8} />
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: `calc(13px * var(--font-scale))`, fontWeight: '700', color: isDragging ? '#2563eb' : '#374151' }}>
                  {isDragging ? 'Solte o arquivo aqui' : 'Arraste e solte ou clique para selecionar'}
                </div>
                <div style={{ fontSize: `calc(11px * var(--font-scale))`, color: '#9ca3af', marginTop: '3px' }}>PNG, JPG ou GIF</div>
              </div>
              <input
                id="logo-file-input"
                className="logo-upload-input"
                type="file"
                onChange={handleFileChange}
                accept="image/png, image/jpeg, image/gif"
              />
            </label>

            {/* Arquivo selecionado */}
            {selectedFile && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 14px', borderRadius: '9px', backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0', marginBottom: '16px' }}>
                <SvgIcon d={ICONS.check} size={14} color="#16a34a" stroke={2.5} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: `calc(12px * var(--font-scale))`, fontWeight: '700', color: '#15803d', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {selectedFile.name}
                  </div>
                  <div style={{ fontSize: `calc(11px * var(--font-scale))`, color: '#16a34a' }}>
                    {(selectedFile.size / 1024).toFixed(1)} KB
                  </div>
                </div>
              </div>
            )}

            {/* Erro */}
            {error && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 14px', borderRadius: '9px', backgroundColor: '#fef2f2', border: '1px solid #fecaca', marginBottom: '16px' }}>
                <svg width="14" height="14" fill="none" stroke="#dc2626" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span style={{ fontSize: `calc(12px * var(--font-scale))`, color: '#dc2626' }}>{error}</span>
              </div>
            )}

            {/* Botão salvar */}
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button
                type="submit"
                disabled={isLoading || !selectedFile}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: '8px',
                  padding: '10px 24px', borderRadius: '10px', border: 'none',
                  background: (isLoading || !selectedFile) ? '#e5e7eb' : 'linear-gradient(135deg,#1e3a5f,#2563eb)',
                  color: (isLoading || !selectedFile) ? '#9ca3af' : '#fff',
                  fontSize: `calc(13px * var(--font-scale))`, fontWeight: '700', fontFamily: 'inherit',
                  cursor: (isLoading || !selectedFile) ? 'not-allowed' : 'pointer',
                  transition: 'opacity .15s',
                }}
              >
                {isLoading ? (
                  <>
                    <Spinner as="span" animation="border" size="sm" style={{ width: '14px', height: '14px', borderWidth: '2px' }} />
                    Enviando...
                  </>
                ) : (
                  <>
                    <SvgIcon d={ICONS.upload} size={14} color="#fff" stroke={2} />
                    Salvar Novo Logo
                  </>
                )}
              </button>
            </div>
          </div>

        </div>
      </form>
    </div>
  );
};

export default LayoutEntidadeTab;
